<template>
  <!-- Loading: shimmer skeleton (label + title + description) -->
  <div
    v-if="isLoading"
    class="module-message module-message--loading flex flex-col gap-size-3"
    aria-hidden="true"
  >
    <div class="module-message__shimmer module-message__shimmer--label"></div>
    <div class="module-message__text flex flex-col gap-size-2">
      <div class="module-message__shimmer module-message__shimmer--title"></div>
      <div
        class="module-message__shimmer module-message__shimmer--description"
      ></div>
    </div>
  </div>

  <div
    v-else
    class="module-message flex flex-col gap-size-3"
    :class="{ 'module-message--inverted': isInverted }"
  >
    <span
      v-if="label"
      class="module-message__label caption-medium"
      aria-label="Label"
    >
      {{ label }}
    </span>

    <div class="module-message__text flex flex-col gap-size-2">
      <component
        v-if="title && isSmall"
        :is="normalizedTitleHeadingTag"
        class="module-message__title heading-bold"
      >
        {{ title }}
      </component>
      <template v-else-if="title">
        <component
          :is="isDesktop ? 'p' : normalizedTitleHeadingTag"
          class="module-message__title module-message__title--mobile heading-jumbo"
        >
          {{ title }}
        </component>
        <component
          :is="isDesktop ? normalizedTitleHeadingTag : 'p'"
          class="module-message__title module-message__title--desktop heading-large"
        >
          {{ title }}
        </component>
      </template>
      <component
        v-if="description"
        :is="normalizedDescriptionHeadingTag"
        class="module-message__description"
        :class="descriptionClass"
      >
        {{ description }}
      </component>
    </div>

    <div
      v-if="extraTitle || extraDescription"
      class="module-message__extra flex flex-col gap-size-2"
    >
      <p
        v-if="extraTitle"
        class="module-message__extra-title"
        :class="extraTitleClass"
      >
        {{ extraTitle }}
      </p>
      <p
        v-if="extraDescription"
        class="module-message__extra-description caption-medium"
      >
        {{ extraDescription }}
      </p>
    </div>

    <div
      v-if="primaryLabel || secondaryLabel"
      class="module-message__actions flex flex-wrap gap-size-4 pt-size-7"
    >
      <BaseButton
        v-if="primaryLabel"
        :label="primaryLabel"
        :class="[buttonThemeClass, 'primary']"
        @click="$emit('primary-click', $event)"
      />
      <BaseButton
        v-if="secondaryLabel"
        :label="secondaryLabel"
        :class="[buttonThemeClass, 'secondary']"
        @click="$emit('secondary-click', $event)"
      />
    </div>
  </div>
</template>
<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { BaseButton } from "ca-online-design-system";
import { normalizeHeadingTag } from "../../../utils/heading";

defineOptions({
  name: "CaModuleMessage",
});

const props = defineProps({
  /** Small pill label shown at the top. */
  label: {
    type: String,
    default: "",
  },
  /** Main title. */
  title: {
    type: String,
    default: "",
  },
  /** Semantic element used for the main title without changing its visual size. */
  titleHeadingType: {
    type: String,
    default: "p",
  },
  /** Supporting description shown below the title. */
  description: {
    type: String,
    default: "",
  },
  descriptionHeadingType: {
    type: String,
    default: "Paragraph",
  },
  /** Secondary (extra) title shown below the description. */
  extraTitle: {
    type: String,
    default: "",
  },
  /** Secondary (extra) description shown below the extra title. */
  extraDescription: {
    type: String,
    default: "",
  },
  /** Label for the primary (filled) action button. */
  primaryLabel: {
    type: String,
    default: "",
  },
  /** Label for the secondary (outline) action button. */
  secondaryLabel: {
    type: String,
    default: "",
  },
  /** Visual size variant: "S" or "M". Controls the typography scale. */
  size: {
    type: String,
    default: "M",
    validator: (value) => ["S", "M"].includes(String(value).toUpperCase()),
  },
  /** Color theme: "default" (light) or "inverted" (dark surface). */
  theme: {
    type: String,
    default: "default",
    validator: (value) =>
      ["default", "inverted"].includes(String(value).toLowerCase()),
  },
});

defineEmits(["primary-click", "secondary-click"]);

/**
 * Estado de loading derivado internamente: enquanto o conteúdo essencial
 * (o título) ainda não chegou, mostra o shimmer. Assim que os dados
 * estiverem disponíveis, renderiza o conteúdo normal sem placeholders.
 */
const isLoading = computed(() => !props.title);

const isDesktop = ref(false);

const updateScreen = () => {
  isDesktop.value = window.innerWidth >= 1025;
};

onMounted(() => {
  updateScreen();
  window.addEventListener("resize", updateScreen);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateScreen);
});

const isSmall = computed(() => String(props.size).toUpperCase() === "S");
const isInverted = computed(
  () => String(props.theme).toLowerCase() === "inverted",
);
const normalizedTitleHeadingTag = computed(() =>
  normalizeHeadingTag(props.titleHeadingType, "p"),
);
const normalizedDescriptionHeadingTag = computed(() =>
  normalizeHeadingTag(props.descriptionHeadingType, "p"),
);

const buttonThemeClass = computed(() =>
  isInverted.value ? "inverted" : "theme",
);

const titleClass = computed(() => {
  if (isSmall.value) return "heading-bold";
  return isDesktop.value ? "heading-large" : "heading-jumbo";
});
const descriptionClass = computed(() => {
  if (isSmall.value)
    return isDesktop.value ? "caption-large" : "caption-medium";
  return "paragraph-small";
});

const extraTitleClass = computed(() =>
  isSmall.value ? "caption-action" : "paragraph-medium",
);
</script>
<style scoped lang="scss">
// ---------------------------------------------------------------------------
// Loading shimmer skeleton. Self-contained: gradient and animation are defined
// locally. The --ca-shimmer-* custom properties allow an ancestor (e.g. a
// tinted section) to override the colours; the fallbacks keep it working
// standalone.
// ---------------------------------------------------------------------------
.module-message__shimmer {
  border-radius: var(--size-3);
  background-image: linear-gradient(
    var(--ca-shimmer-angle, 90deg),
    var(--ca-shimmer-from, var(--ca-color-empresas-solarized-medium, #edecea))
      0%,
    var(--ca-shimmer-to, var(--ca-color-backgrounds-container)) 100%
  );
  background-size: 200% 100%;
  animation: ca-module-message-shimmer 1.5s ease-in-out infinite;

  &--label {
    width: 148px;
    height: 38px;
    border-radius: 100px;
  }

  &--title {
    width: 70%;
    height: 60px;
    border-radius: 8px;
  }

  &--description {
    width: 70%;
    height: 26px;
    border-radius: 8px;
  }
}

@keyframes ca-module-message-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 767.98px) {
  .module-message__text {
    gap: var(--size-3);
  }
}

@media (min-width: 1025px) {
  .module-message__text {
    gap: var(--size-4);
  }
}

.module-message__label {
  display: flex;
  align-self: flex-start;
  align-items: center;
  padding: var(--size-3) var(--size-4);
  border-radius: 100px;
  background: var(--ca-color-neutrals-on-background-low-10);
  color: var(--ca-color-text-body);
  text-transform: uppercase;
}

.module-message__title {
  margin: 0;
  color: var(--ca-color-text-headers);
}

.module-message__title--desktop {
  display: none;
}

@media (min-width: 1025px) {
  .module-message__title--mobile {
    display: none;
  }

  .module-message__title--desktop {
    display: block;
  }
}

.module-message__description {
  margin: 0;
  color: var(--ca-color-text-body-variant);
}

.module-message__extra-title {
  margin: 0;
  color: var(--ca-color-text-headers);
}

.module-message__extra-description {
  margin: 0;
  color: var(--ca-color-text-body);
}

// ---------------------------------------------------------------------------
// Inverted theme (dark surface) — matches the design's green variant.
// ---------------------------------------------------------------------------
.module-message--inverted {
  .module-message__label {
    background: var(--ca-color-backgrounds-surface-10);
    color: var(--ca-color-text-inverted);
  }

  .module-message__title,
  .module-message__extra-title {
    color: var(--ca-color-text-inverted);
  }

  .module-message__description,
  .module-message__extra-description {
    color: var(--ca-color-text-inverted);
  }
}
</style>
